import { useEffect, useRef } from 'react'
import type { Direction, GameSettings, GameState, Point } from '../game/types'

interface Props {
  state: GameState
  settings: GameSettings
  label: string
  onDirection: (direction: Direction) => void
}

const hexToRgba = (hex: string, alpha: number) => {
  const value = Number.parseInt(hex.slice(1), 16)
  return `rgba(${value >> 16}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, radius: number) {
  ctx.beginPath()
  ctx.roundRect(x, y, size, size, radius)
}

function drawPoint(ctx: CanvasRenderingContext2D, point: Point, cell: number, gap: number, style: GameSettings['appearance']['bodyStyle']) {
  const inset = gap / 2
  const size = cell - gap
  const x = point.x * cell + inset
  const y = point.y * cell + inset
  if (style === 'orb') {
    ctx.beginPath()
    ctx.arc(x + size / 2, y + size / 2, size * 0.44, 0, Math.PI * 2)
  } else {
    roundedRect(ctx, x, y, size, style === 'block' ? Math.max(1, cell * 0.05) : cell * 0.28)
  }
}

export function GameCanvas({ state, settings, label, onDirection }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const stateRef = useRef(state)
  const settingsRef = useRef(settings)
  const pointerStart = useRef<Point | null>(null)

  useEffect(() => { stateRef.current = state }, [state])
  useEffect(() => { settingsRef.current = settings }, [settings])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    let frame = 0

    const render = (time: number) => {
      const current = stateRef.current
      const config = settingsRef.current
      const rect = canvas.getBoundingClientRect()
      const ratio = Math.min(2, window.devicePixelRatio || 1)
      const width = Math.max(1, Math.round(rect.width * ratio))
      const height = Math.max(1, Math.round(rect.height * ratio))
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width
        canvas.height = height
      }
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
      const cell = Math.min(rect.width / config.rules.columns, rect.height / config.rules.rows)
      const boardWidth = cell * config.rules.columns
      const boardHeight = cell * config.rules.rows
      const offsetX = (rect.width - boardWidth) / 2
      const offsetY = (rect.height - boardHeight) / 2
      ctx.clearRect(0, 0, rect.width, rect.height)
      ctx.save()
      ctx.translate(offsetX, offsetY)
      ctx.fillStyle = config.appearance.background
      ctx.fillRect(0, 0, boardWidth, boardHeight)

      if (config.appearance.gridOpacity > 0) {
        ctx.strokeStyle = hexToRgba(config.appearance.grid, config.appearance.gridOpacity / 260)
        ctx.lineWidth = 1
        ctx.beginPath()
        for (let x = 1; x < config.rules.columns; x += 1) {
          ctx.moveTo(x * cell, 0)
          ctx.lineTo(x * cell, boardHeight)
        }
        for (let y = 1; y < config.rules.rows; y += 1) {
          ctx.moveTo(0, y * cell)
          ctx.lineTo(boardWidth, y * cell)
        }
        ctx.stroke()
      }

      ctx.fillStyle = hexToRgba(config.appearance.grid, 0.62)
      current.obstacles.forEach((point) => {
        const inset = cell * 0.18
        roundedRect(ctx, point.x * cell + inset, point.y * cell + inset, cell - inset * 2, cell * 0.12)
        ctx.fill()
      })

      const pulse = config.accessibility.reducedMotion ? 1 : 0.88 + Math.sin(time / 160) * 0.1
      ctx.shadowColor = config.appearance.food
      ctx.shadowBlur = cell * config.appearance.glow / 42
      ctx.fillStyle = config.appearance.food
      current.foods.forEach((point) => {
        const radius = cell * 0.25 * pulse
        const cx = (point.x + 0.5) * cell
        const cy = (point.y + 0.5) * cell
        ctx.save()
        ctx.translate(cx, cy)
        ctx.rotate(Math.PI / 4)
        roundedRect(ctx, -radius, -radius, radius * 2, radius * 0.28)
        ctx.fill()
        ctx.restore()
      })

      if (config.appearance.trail) {
        current.snake.slice(1).reverse().forEach((point, reverseIndex) => {
          const alpha = 0.08 + (reverseIndex / Math.max(1, current.snake.length)) * 0.18
          ctx.fillStyle = hexToRgba(config.appearance.snake, alpha)
          drawPoint(ctx, point, cell, cell * 0.02, config.appearance.bodyStyle)
          ctx.fill()
        })
      }

      current.snake.forEach((point, index) => {
        const strength = 0.5 + 0.5 * (1 - index / Math.max(1, current.snake.length))
        ctx.shadowColor = config.appearance.snake
        ctx.shadowBlur = cell * config.appearance.glow / (index === 0 ? 38 : 72)
        ctx.fillStyle = hexToRgba(config.appearance.snake, 0.66 + strength * 0.34)
        drawPoint(ctx, point, cell, cell * (index === 0 ? 0.12 : 0.18), config.appearance.bodyStyle)
        ctx.fill()
      })

      const head = current.snake[0]
      if (head) {
        ctx.shadowBlur = 0
        ctx.fillStyle = config.appearance.background
        const eye = Math.max(1.5, cell * 0.075)
        const vector = current.direction === 'left' ? [-1, 0] : current.direction === 'right' ? [1, 0] : current.direction === 'up' ? [0, -1] : [0, 1]
        const side = [-vector[1], vector[0]]
        for (const sign of [-1, 1]) {
          ctx.beginPath()
          ctx.arc((head.x + 0.5 + vector[0] * 0.17 + side[0] * 0.16 * sign) * cell, (head.y + 0.5 + vector[1] * 0.17 + side[1] * 0.16 * sign) * cell, eye, 0, Math.PI * 2)
          ctx.fill()
        }
      }
      ctx.restore()
      frame = requestAnimationFrame(render)
    }
    frame = requestAnimationFrame(render)
    return () => cancelAnimationFrame(frame)
  }, [])

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    pointerStart.current = { x: event.clientX, y: event.clientY }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const start = pointerStart.current
    pointerStart.current = null
    if (!start) return
    const dx = event.clientX - start.x
    const dy = event.clientY - start.y
    if (Math.max(Math.abs(dx), Math.abs(dy)) < settings.controls.swipeThreshold) return
    onDirection(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up'))
  }

  return <canvas ref={canvasRef} className="game-canvas" aria-label={label} role="img" onPointerDown={onPointerDown} onPointerUp={onPointerUp} />
}
